import { Box, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useAuthState } from 'state/useAuthState';
import { API } from 'api';
import { LatLng } from 'shared/types';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { useContext } from 'react';
import { useTabs } from 'hooks/useTabs';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { useMapURLParams } from 'components/IncidentsMap/useMapURLParams';
import { BroadcastifyFeeds } from 'features/broadcastify/components/BroadcastifyFeeds';
import { MapEntityDrawerHeader } from '../MapEntityDrawerHeader';
import { MapEntityDrawerContext } from '../MapEntityDrawer';
import { MapPinInfo } from './MapPinInfo';

type LatLngMetadata = {
  region: {
    id: number;
    displayName: string;
    state: string;
    name: string; // AKA "slug"
  } | null;
  slackChannel: string;
  slackChannelId: string | null; // Can be null for archived channels
};

const TAB_PANEL_ID_PREFIX = 'map-pin-details';

const fetchMetadata = (
  coordinates: LatLng,
): Promise<AxiosResponse<LatLngMetadata>> =>
  API.get<LatLngMetadata>(`/lat_lng/metadata/`, {
    params: { lat: coordinates.lat, lng: coordinates.lng },
  });

export const MapPinDrawerContent = (): JSX.Element => {
  const { latitudeParam, longitudeParam } = useMapURLParams();
  const { t } = useTranslation();
  const { showMembershipProFeatures } = useAuthState();
  const { scrollElementRef } = useContext(MapEntityDrawerContext);

  const metaDataQuery = useQuery({
    queryKey: ['metadata', latitudeParam, longitudeParam],
    queryFn: () => fetchMetadata({ lat: latitudeParam, lng: longitudeParam }),
    enabled: showMembershipProFeatures && !!latitudeParam && !!longitudeParam,
  });

  const { isLoading, data } = metaDataQuery;

  const countyName = data?.data.region?.displayName ?? '...';
  const slackChannel = data?.data.slackChannel;
  const slackChannelId = data?.data.slackChannelId;

  const tabData = [
    {
      tabId: 'info',
      label: t('mapPin.tabs.info'),
      enabled: true,
      content: (
        <MapPinInfo
          coordinates={{ lat: latitudeParam, lng: longitudeParam }}
          slackChannel={slackChannel}
          slackChannelId={slackChannelId}
        />
      ),
    },
    {
      tabId: 'broadcastify',
      label: t('mapPin.tabs.broadcastify'),
      enabled: true,
      content: (
        <BroadcastifyFeeds
          lat={latitudeParam}
          lng={longitudeParam}
          withHeader={false}
        />
      ),
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('geoEvent.tabsLabel'),
    tabData,
  });

  if (isLoading) {
    return <LoadingAndErrors isLoading />;
  }

  return (
    <Box>
      <DrawerStickyHeader
        scrollContainer={scrollElementRef?.current as HTMLElement}
      >
        <DrawerStickyHeader.ContentContainer>
          <MapEntityDrawerHeader>{countyName}</MapEntityDrawerHeader>
        </DrawerStickyHeader.ContentContainer>

        <Tabs {...tabsProps}>
          {tabProps.map((tProps) => (
            <Tab
              key={tProps.key}
              {...tProps}
              sx={{
                maxWidth: 1 / tabData.filter((tab) => tab.enabled).length,
              }}
            />
          ))}
        </Tabs>
      </DrawerStickyHeader>

      {tabPanels}
    </Box>
  );
};
